import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { endSession, freshAccessToken, getRefreshToken, isSessionRejectedCode, renewAccessToken } from './session';

const signOut = () => {
  endSession();
  const loginPath = `${process.env.PUBLIC_URL ?? ''}/login`;
  if (window.location.pathname !== loginPath) window.location.assign(loginPath);
};

type SessionRequest = AxiosRequestConfig & { skipAuth?: boolean; sessionRetried?: boolean };

const authorise = async (request: SessionRequest) => {
  if (request.skipAuth) return request;

  const token = await freshAccessToken();
  if (!token) {
    signOut();
    throw new axios.Cancel('No active session');
  }

  request.headers = { ...request.headers, token, Authorization: `Bearer ${token}` };
  return request;
};

/**
 * Retries a request the API rejected as unauthenticated, renewing the access token it carried
 * unless something else already has. Resolves to null when there is nothing left to retry with.
 */
const retryWithRenewedToken = async (api: AxiosInstance, request: SessionRequest, originalError: unknown) => {
  if (request.skipAuth || request.sessionRetried || !getRefreshToken()) return null;

  let token: string | null;
  try {
    token = await renewAccessToken(request.headers?.token ?? null);
  } catch {
    throw originalError;
  }
  if (!token) return null;

  return api({ ...request, sessionRetried: true });
};

const isUnauthenticated = (error: any) =>
  error.response?.status === 401 || isSessionRejectedCode(error.response?.data?.error?.code);

const retryOrSignOut = (api: AxiosInstance) => async (error: any) => {
  if (axios.isCancel(error)) throw error;

  if (isUnauthenticated(error)) {
    const retried = await retryWithRenewedToken(api, error.config, error);
    if (retried) return retried;
    signOut();
  }

  throw error;
};

const client = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  responseType: 'json',
  headers: {
    'client-id': process.env.REACT_APP_CLIENT_ID,
  },
});

client.interceptors.request.use(authorise);

client.interceptors.response.use(
  async response => {
    const error = response.data?.error;
    if (!error) return response;

    // Legacy answers some rejections with HTTP 200 and an error body.
    if (isSessionRejectedCode(error.code)) {
      const rejection = new Error(error.message);
      const retried = await retryWithRenewedToken(client, response.config, rejection);
      if (retried) return retried;
      signOut();
      throw rejection;
    }

    throw new Error(error.message);
  },
  retryOrSignOut(client)
);

export const coreApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  responseType: 'json',
  headers: {
    'Content-Type': 'application/json',
    'client-id': process.env.REACT_APP_CLIENT_ID
  }
});

export const geoApi = axios.create({
  baseURL: process.env.REACT_APP_POSTCODE_API,
  responseType: 'json'
});

coreApi.interceptors.request.use(authorise);

coreApi.interceptors.response.use(response => response, retryOrSignOut(coreApi));

export default client;
